import * as Yup from 'yup';
import { isPhoneValidForForm } from '@components/atoms/ui-kit/phone-input.tsx';
import type { TFunction } from 'i18next';

export const BATCH_PRIORITY_OPTIONS = [
  {
    value: 50,
    label: 'Premium / Internal',
    description: 'Ourselves + enterprise customers (~15 min)'
  },
  {
    value: 100,
    label: 'Good Customers',
    description: 'High-value paying customers (~30 min)'
  },
  {
    value: 150,
    label: 'Standard Paying',
    description: 'Regular subscriptions (~45 min)'
  },
  {
    value: 200,
    label: 'Normal (DEFAULT)',
    description: 'Baseline - no special treatment (~1.5 hours)'
  },
  {
    value: 250,
    label: 'Low Priority',
    description: 'Low priority accounts (~3 hours)'
  },
  {
    value: 300,
    label: 'Lowest Priority',
    description: 'Archived/inactive accounts (~6+ hours)'
  }
] as const;

export const DEFAULT_BATCH_PRIORITY = 200;

export function buildValidationSchema(t: TFunction) {
  const optionalText = () =>
    Yup.string()
      .transform(v => (v?.trim?.() === '' ? null : v))
      .nullable()
      .notRequired();

  return Yup.object().shape({
    name: Yup.string().required(t('name-is-required')),
    email: Yup.string()
      .email(t('invalid-email'))
      .required(t('email-is-required')),
    phone: Yup.string()
      .nullable()
      .transform(value => {
        if (!value) return null;
        const trimmed = value.trim();
        if (trimmed === '') return null;
        // react-international-phone emits just the country dial code (e.g. "+31")
        // when the field is empty — treat these as no phone provided
        if (/^\+\d{1,4}$/.test(trimmed)) return null;
        return value;
      })
      .test(
        'is-valid-phone',
        t('enter-a-valid-international-phone-number'),
        isPhoneValidForForm
      )
      .notRequired(),
    is_active: Yup.boolean().optional(),
    is_reseller: Yup.boolean().optional(),
    account_manager: optionalText(),
    billing_contact_firstname: Yup.string().optional(),
    billing_contact_lastname: Yup.string().optional(),
    billing_vat: Yup.string().optional(),
    billing_ref: Yup.string().optional(),
    invoice_currency: Yup.string().oneOf(['EUR', 'USD']).optional(),
    receive_invoice_by_email: Yup.boolean().optional(),
    address_street: Yup.string().optional(),
    address_postal_code: Yup.string().optional(),
    address_city: Yup.string().optional(),
    address_country_code: Yup.string().optional(),
    max_users: Yup.number().optional(),
    brand_color: Yup.string()
      .nullable()
      .transform(value => (value?.trim() === '' ? null : value))
      .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, {
        message: t('invalid-color'),
        excludeEmptyString: true
      })
      .optional(),
    reseller_transcript_access: Yup.number().optional(),
    use_data_consent: Yup.boolean().optional(),
    default_integration_id: Yup.number().optional(),
    logo: Yup.string().optional(),
    diarization: Yup.boolean().optional(),
    microsoft_tid: Yup.string()
      .nullable()
      .test(
        'is-valid-uuid',
        t('typed-value-was-not-correct'),
        value =>
          !value ||
          /^(?:urn:uuid:)?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
            value
          )
      )
      .optional(),
    mollie_id: Yup.string()
      .nullable()
      .test(
        'is-valid-mollie-id',
        t('typed-value-was-not-correct'),
        value => !value || /^cst_[a-zA-Z0-9]+$/.test(value)
      )
      .optional(),
    jortt_id: Yup.string()
      .nullable()
      .test(
        'is-valid-uuid',
        t('typed-value-was-not-correct'),
        value =>
          !value ||
          /^(?:urn:uuid:)?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
            value
          )
      )
      .optional(),
    wefact_id: Yup.string()
      .nullable()
      .test(
        'is-valid-wefact-id',
        t('typed-value-was-not-correct'),
        value => !value || /^[1-9][0-9]*$/.test(value)
      )
      .optional(),
    batch_priority: Yup.number()
      .nullable()
      .integer(t('batch-priority-must-be-integer'))
      .min(1, t('batch-priority-min-value'))
      .transform(value => (Number.isNaN(value) ? null : value))
      .optional()
  });
}
